/**
 * AuditDetailPanel Component
 *
 * Feature: 123-audit-log-viewer
 * Description: Slide-out detail drawer for audit log entries
 * Constitution: v1.12.0
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Drawer } from '../../../components/ui';
import type { SystemAuditLogEntry, AuthAuditLogEntry } from '../../../types/audit';
import { resolveNames } from '../../../services/auditViewerApi';
import ChangesDiff, { extractGuids } from './ChangesDiff';
import { formatEastern } from '@/utils/dateFormatter';

type AuditEntry =
  | (SystemAuditLogEntry & { _type: 'system' })
  | (AuthAuditLogEntry & { _type: 'auth' });

interface AuditDetailPanelProps {
  entry: AuditEntry | null;
  open: boolean;
  onClose: () => void;
}

function formatTimestamp(isoString: string): { display: string; utc: string } {
  const date = new Date(isoString);
  return {
    display: formatEastern(date),
    utc: date.toISOString(),
  };
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <span className="block text-xs text-[var(--color-text-secondary)]">{label}</span>
      <div className="break-all text-sm text-[var(--color-text-primary)]">{value}</div>
    </div>
  );
}

function SystemAuditDetail({ entry }: { entry: SystemAuditLogEntry }) {
  const ts = formatTimestamp(entry.createdAt);

  // Gather every GUID in the entry (id + change payloads) and resolve them to friendly names so the
  // detail shows "Chemical Labels" / "503B Paginated" instead of raw GUIDs.
  const guids = useMemo(() => {
    const set = new Set<string>();
    extractGuids(entry.entityId).forEach((g) => set.add(g));
    (entry.changes ?? []).forEach((c) => {
      extractGuids(c.oldValue).forEach((g) => set.add(g));
      extractGuids(c.newValue).forEach((g) => set.add(g));
    });
    return [...set];
  }, [entry]);
  const namesQ = useQuery({
    queryKey: ['audit', 'resolve-names', [...guids].sort()],
    queryFn: () => resolveNames(guids),
    enabled: guids.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const nameMap = namesQ.data ?? {};
  const entityIdName = entry.entityId ? nameMap[entry.entityId.toLowerCase()] : undefined;

  return (
    <div className="flex flex-col gap-4">
      <DetailRow label="Timestamp" value={<span title={ts.utc}>{ts.display}</span>} />
      <DetailRow label="User Email" value={entry.userEmail} />
      <DetailRow
        label="Action"
        value={
          <Badge
            variant={
              entry.action === 'create' ? 'success' : entry.action === 'delete' ? 'error' : 'info'
            }
            size="sm"
          >
            {entry.action}
          </Badge>
        }
      />
      <DetailRow label="Entity Type" value={entry.entityType} />
      <DetailRow
        label="Entity ID"
        value={
          <span>
            {entry.entityId}
            {entityIdName && <span className="ml-1 font-medium text-[var(--color-accent)]">· {entityIdName}</span>}
          </span>
        }
      />
      <DetailRow label="Entity Name" value={entry.entityName} />
      <DetailRow label="IP Address" value={entry.ipAddress} />
      <DetailRow label="User Agent" value={entry.userAgent} />
      <DetailRow label="Client Account ID" value={entry.clientAccountId} />
      {entry.changes && entry.changes.length > 0 && (
        <div>
          <span className="mb-1 block text-xs text-[var(--color-text-secondary)]">Changes</span>
          <ChangesDiff changes={entry.changes} nameMap={nameMap} />
        </div>
      )}
    </div>
  );
}

function AuthAuditDetail({ entry }: { entry: AuthAuditLogEntry }) {
  const ts = formatTimestamp(entry.timestamp);

  let additionalDataDisplay: React.ReactNode = null;
  if (entry.additionalData) {
    if (
      typeof entry.additionalData === 'object' &&
      '_parseError' in entry.additionalData
    ) {
      additionalDataDisplay = (
        <div>
          <span className="text-xs text-[var(--color-status-danger)]">
            (JSON parse error — showing raw data)
          </span>
          <div className="whitespace-pre-wrap font-mono text-xs text-[var(--color-text-primary)]">
            {String(entry.additionalData._raw || '')}
          </div>
        </div>
      );
    } else {
      additionalDataDisplay = (
        <div className="whitespace-pre-wrap font-mono text-xs text-[var(--color-text-primary)]">
          {JSON.stringify(entry.additionalData, null, 2)}
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <DetailRow label="Timestamp" value={<span title={ts.utc}>{ts.display}</span>} />
      <DetailRow label="Event Type" value={entry.eventType} />
      <DetailRow label="User Email" value={entry.userEmail} />
      <DetailRow label="User ID" value={entry.userId} />
      <DetailRow
        label="Result"
        value={
          <Badge variant={entry.success ? 'success' : 'error'} size="sm">
            {entry.success ? 'Success' : 'Failed'}
          </Badge>
        }
      />
      <DetailRow label="Failure Reason" value={entry.failureReason} />
      <DetailRow label="IP Address" value={entry.ipAddress} />
      <DetailRow label="User Agent" value={entry.userAgent} />
      {additionalDataDisplay && (
        <div>
          <span className="mb-1 block text-xs text-[var(--color-text-secondary)]">
            Additional Data
          </span>
          {additionalDataDisplay}
        </div>
      )}
    </div>
  );
}

export default function AuditDetailPanel({ entry, open, onClose }: AuditDetailPanelProps) {
  return (
    <Drawer
      isOpen={open}
      onClose={onClose}
      title={entry?._type === 'system' ? 'Audit Log Entry' : 'Auth Event Details'}
      width="md"
    >
      {entry?._type === 'system' && <SystemAuditDetail entry={entry} />}
      {entry?._type === 'auth' && <AuthAuditDetail entry={entry} />}
    </Drawer>
  );
}
